import React, { useRef, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { API_BASE } from "../../utils/api";
import { toast, ToastContainer } from "react-toastify";
import ProductSearch from "../Takeaway/components/ProductSearch";
import PageHeader from "../../components/common/PageHeader";
import ShadowContainer from "../../components/common/ShadowContainer";
import { useRestaurantDetails } from "../../context/RestaurantDetailsContext";

const getProductDisplayName = (p) => p?.productName?.en || p?.productName || "Unknown";

const buildDefaultComboDescription = (items) => {
  const parts = (items || [])
    .filter(Boolean)
    .map((p) => `${getProductDisplayName(p)} x${p.qty || 1}`);
  if (!parts.length) return "";
  return `This combo includes ${parts.join(", ")}.`;
};

const ComboAdd = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const { restaurantDetails } = useRestaurantDetails();

  const [comboName, setComboName] = useState("");
  const [comboPrice, setComboPrice] = useState("");
  const [description, setDescription] = useState("");
  const [addedProducts, setAddedProducts] = useState([]); // [{...product, qty}]
  const [isVisible, setIsVisible] = useState(true);
  const [packageCharge, setPackageCharge] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState("");
  const [isDescriptionAuto, setIsDescriptionAuto] = useState(!isEdit);
  const lastAutoDescriptionRef = useRef("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isEdit) {
      fetchComboDetails();
    }
  }, [isEdit]);

  useEffect(() => {
    const nextAuto = buildDefaultComboDescription(addedProducts);
    const shouldAutoUpdate = isDescriptionAuto || description === lastAutoDescriptionRef.current;
    if (!shouldAutoUpdate) return;
    lastAutoDescriptionRef.current = nextAuto;
    setDescription(nextAuto);
  }, [addedProducts, isDescriptionAuto, description]);

  const fetchComboDetails = async () => {
    try {
      const response = await api.get(`/combos/${id}`);
      const data = response.data;
      setComboName(data.productName?.en || "");
      setComboPrice(data.price || "");
      setPackageCharge(
        data.packageCharge !== undefined && data.packageCharge !== null
          ? String(data.packageCharge)
          : "",
      );
      const existingDesc = data.description?.en || "";
      setIsVisible(data.isVisible);
      const itemsFromApi =
        Array.isArray(data.comboProductItems) && data.comboProductItems.length
          ? data.comboProductItems.map((x) => ({
              ...(x.product || {}),
              qty: x.qty || 1,
            }))
          : (data.comboProducts || []).map((p) => ({ ...p, qty: 1 }));
      setAddedProducts(itemsFromApi);
      setExistingImageUrl(data.imageUrl || "");

      const autoDesc = buildDefaultComboDescription(itemsFromApi);
      lastAutoDescriptionRef.current = autoDesc;
      const shouldAuto = !existingDesc || existingDesc.trim() === "" || existingDesc === autoDesc;
      setIsDescriptionAuto(shouldAuto);
      setDescription(shouldAuto ? autoDesc : existingDesc);
    } catch (error) {
      toast.error("Failed to load combo details");
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleAddProduct = (product) => {
    if (!product) return;
    
    // Check if already added
    if (addedProducts.some((p) => p._id === product._id)) {
      toast.warning("Product already added");
      return;
    }
    
    setAddedProducts([...addedProducts, { ...product, qty: 1 }]);
  };

  const removeProduct = (productId) => {
    setAddedProducts(addedProducts.filter((p) => p._id !== productId));
  };

  const updateQty = (productId, nextQty) => {
    const qtyNum = Number(nextQty);
    const qty = Number.isFinite(qtyNum) ? Math.max(1, Math.floor(qtyNum)) : 1;
    setAddedProducts((prev) =>
      prev.map((p) => (p._id === productId ? { ...p, qty } : p)),
    );
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (isSaving) return;
    if (!comboName || !comboPrice || !description) {
      toast.error("Please fill all fields.");
      return;
    }
    if (!Array.isArray(addedProducts) || addedProducts.length < 2) {
      toast.error("Please add at least 2 products to create a combo.");
      return;
    }
    if (!imageFile && !existingImageUrl) {
      toast.error("Please upload a combo image.");
      return;
    }

    setIsSaving(true);
    const payload = new FormData();
    payload.append("productName", comboName);
    payload.append("price", comboPrice);
    payload.append("packageCharge", packageCharge === "" ? "0" : packageCharge);
    payload.append("description", description);
    payload.append("isVisible", String(isVisible));
    payload.append(
      "comboProductItems",
      JSON.stringify(
        addedProducts.map((p) => ({
          productId: p._id,
          qty: p.qty || 1,
        })),
      ),
    );
    if (imageFile) payload.append("image", imageFile);

    try {
      if (isEdit) {
        await api.put(`/combos/${id}`, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Combo updated successfully. Redirecting...");
      } else {
        await api.post("/combos", payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Combo created successfully. Redirecting...");
      }
      setTimeout(() => navigate("/combos"), 3000);
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        (isEdit ? "Failed to update combo" : "Failed to create combo");
      toast.error(msg);
      setIsSaving(false);
    }
  };

  return (
    <div className="container">
      <ToastContainer position="top-center" autoClose={3000} />

      <PageHeader
        title={isEdit ? "Edit Combo" : "Add Combo"}
        subtitle={
          <span className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 bg-blue-500 rounded-full"></span>
            {isEdit ? "Update combo details" : "Create a new combo for your menu"}
          </span>
        }
        showBackButton={true}
        backTo={-1}
      />

      <ShadowContainer>
        <form className="form p-4 sm:p-6" onSubmit={handleSave}>
          {/* Combo Name */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Combo Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              value={comboName}
              onChange={(e) => setComboName(e.target.value)}
              placeholder="Enter combo name"
            />
          </div>

          {/* Product Search */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search & Add Product <span className="text-red-500">*</span>
            </label>
            <ProductSearch onSelect={handleAddProduct} />
          </div>

          {/* Added Products Table */}
          <div className="mb-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="w-full overflow-x-auto">
                <table className="min-w-[640px] w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Product Name
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Price
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quantity
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {addedProducts.map((product) => (
                      <tr key={product._id}>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {getProductDisplayName(product)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {restaurantDetails?.currency?.symbol || "₹"}
                          {product.offerPrice || product.price}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          <div className="inline-flex items-center gap-2">
                            <button
                              type="button"
                              className="w-8 h-8 rounded-md border border-gray-200 bg-white hover:bg-gray-50 text-gray-700"
                              onClick={() => updateQty(product._id, (product.qty || 1) - 1)}
                              aria-label="Decrease quantity"
                            >
                              -
                            </button>
                            <input
                              type="number"
                              min="1"
                              className="w-16 px-2 py-1 border border-gray-300 rounded-md text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              value={product.qty || 1}
                              onChange={(e) => updateQty(product._id, e.target.value)}
                            />
                            <button
                              type="button"
                              className="w-8 h-8 rounded-md border border-gray-200 bg-white hover:bg-gray-50 text-gray-700"
                              onClick={() => updateQty(product._id, (product.qty || 1) + 1)}
                              aria-label="Increase quantity"
                            >
                              +
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            type="button"
                            className="text-red-600 hover:text-red-900 bg-red-50 p-2 rounded-md hover:bg-red-100 transition-colors"
                            onClick={() => removeProduct(product._id)}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                    {addedProducts.length === 0 && (
                      <tr>
                        <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                          No products added yet. Use the search bar above.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Price + Package Charge */}
          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Combo Price */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Combo Price <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                  {restaurantDetails?.currency?.symbol || "₹"}
                </span>
                <input
                  type="number"
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={comboPrice}
                  onChange={(e) => setComboPrice(e.target.value)}
                  placeholder="0.00"
                />
              </div>
            </div>

            {/* Package Charge */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Package Charge
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                  {restaurantDetails?.currency?.symbol || "₹"}
                </span>
                <input
                  type="number"
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={packageCharge}
                  onChange={(e) => setPackageCharge(e.target.value)}
                  placeholder="0.00"
                  min="0"
                />
              </div>
            </div>
          </div>

          {/* Combo Image */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Combo Image <span className="text-red-500">*</span>
            </label>

            {(imagePreview || existingImageUrl) && (
              <div className="mb-3">
                <img
                  src={imagePreview || `${API_BASE}${existingImageUrl}`}
                  alt="Combo"
                  className="w-48 h-48 object-cover rounded-lg border border-gray-200 shadow-sm"
                />
              </div>
            )}

            <input
              type="file"
              accept="image/png, image/jpeg, image/jpg, image/gif"
              onChange={handleImageChange}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 file:cursor-pointer"
            />
          </div>
          
          {/* Description */}
          <div className="mb-6">
            <div className="flex items-center justify-between gap-3 mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Description <span className="text-red-500">*</span>
              </label>
              <button
                type="button"
                className="text-xs font-semibold text-blue-700 hover:text-blue-800"
                onClick={() => {
                  setIsDescriptionAuto(true);
                  const nextAuto = buildDefaultComboDescription(addedProducts);
                  lastAutoDescriptionRef.current = nextAuto;
                  setDescription(nextAuto);
                }}
              >
                Reset to auto template
              </button>
            </div>
            <textarea
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
              rows={4}
              value={description}
              onChange={(e) => {
                setIsDescriptionAuto(false);
                lastAutoDescriptionRef.current = "";
                setDescription(e.target.value);
              }}
              placeholder="Enter combo description"
            ></textarea>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row justify-end items-center gap-3 pt-6 border-t border-gray-100">
            <button
              type="button"
              className="w-full sm:w-auto px-6 py-2.5 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors duration-200"
              onClick={() => navigate("/combos")}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className={`w-full sm:w-auto px-6 py-2.5 bg-blue-600 text-white font-medium rounded-lg transition-colors duration-200 shadow-sm inline-flex items-center justify-center gap-2 ${
                isSaving ? "opacity-70 cursor-not-allowed" : "hover:bg-blue-700"
              }`}
            >
              {isSaving && (
                <svg
                  className="animate-spin h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  ></path>
                </svg>
              )}
              {isSaving ? "Saving..." : "Save Combo"}
            </button>
          </div>
        </form>
      </ShadowContainer>
    </div>
  );
};

export default ComboAdd;
